import React, { useEffect } from 'react';

function InstructorDrawer({ isOpen, onClose }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
  }, [isOpen]);

  return (
    <>
      <div className={`drawer-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}></div>
      <div className={`drawer ${isOpen ? 'active' : ''}`}>
        <div className="drawer-header">
          <h2>About Michele Piccini</h2>
          <button className="drawer-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="drawer-content">
          <div className="instructor-profile">
            <div className="instructor-image-large">
              MP
            </div>
            <div className="instructor-bio">
              <h2>Michele Piccini</h2>
              <p className="role">Lead JKD Instructor & Academy Director</p>
              <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Hi, I'm Michele, or Mike for friends. My journey in martial arts has been a lifelong passion that began when I was just four years old. Today, as the director of J.K.D. Functional Defense, I'm dedicated to sharing the transformative power of Jeet Kune Do with students from all walks of life.
              </p>
            </div>
          </div>

                      <div className="credentials">
              <h4>Certifications & Training</h4>
              <ul>
                <li>Full Instructor certification under Sifu Dan Inosanto's JKD lineage</li>
                <li>Certified in Filipino Martial Arts (Kali/Escrima)</li>
                <li>Boxing and Muay Thai training (8 years)</li>
                <li>Brazilian Jiu-Jitsu Blue Belt</li>
                <li>First Aid & CPR certified</li>
                <li>Workshops with Tim Tackett, Jerry Poteet, and other original JKD students</li>
              </ul>
            </div>

          <div className="story-section">
            <h3>The beginning</h3>
            <p>
              My passion for martial arts started early. I was only <strong>four years old</strong> when, despite being under the minimum age, I convinced the instructor to let me try a Shotokan Karate class. That day began an <strong>eleven-year journey</strong> filled with discipline, respect, coordination, and personal growth.
            </p>
            <p>
              Through Karate, I learned the fundamentals of martial arts: focus, perseverance, and the importance of a strong foundation. I eventually competed at <strong>national and world-level events</strong>, always pushing beyond my limits. The competitive experience taught me not just how to fight, but how to overcome fear, manage pressure, and maintain composure in high-stakes situations.
            </p>
          </div>

          <div className="story-section">
            <h3>Searching for something more</h3>
            <p>
              Over time, however, I began feeling restricted by the rules: fixed stances, forbidden strikes, controlled contact.
              While preparing for my black belt, I decided to explore something different: <strong>Muay Thai</strong>. A direct, powerful, full-contact style.
              I loved it immediately: the training, the impact, the intensity. And yet… something still felt missing.
            </p>
          </div>

          <div className="story-section">
            <h3>The encounter that changed everything</h3>
            <p>
              Almost by chance, a friend introduced me to <strong>Jeet Kune Do Evolution</strong> — a dynamic, fluid, and free system where logic, instinct, and functionality merge.
              It draws heavily from Wing Chun but also integrates elements from Kali, Boxing, Wrestling, BJJ, Muay Thai, Savate, Judo, Taekwondo, Fencing, and more.
            </p>
            <p>
              During my first trainings and open sparring sessions (1 vs 3, 5 vs 5, all vs all) I realized I had finally found what I'd always been searching for:
              <em> A method without rigid rules, where what works becomes yours, where you remove the unnecessary and keep only what matters.</em>
            </p>
          </div>

          <div className="story-section">
            <h3>From fighting to awareness</h3>
            <p>
              In the following years, I also dedicated myself to <strong>MMA</strong>, competing and studying the dynamics of sport fighting.
              I learned grappling, wrestling, ground control, transitions, and striking under pressure, all tools that would later help me understand instinct, body mechanics, and real-life contact situations.
              But eventually, I understood something important: Although MMA had taught me a lot, it wasn't my true path.
            </p>
            <p>Sport combat has rules, weight classes, timers, and referees. <strong>Functional defense has none of that.</strong> No cage, no rounds, only reality: unpredictable, instinctive, fast.</p>
            <p><em>There, only one thing matters: being ready.</em></p>
          </div>

          <div className="story-section">
            <h3>Why I teach</h3>
            <p>
              I integrated everything I had learned from different disciplines into a realistic, practical, and functional self-defense approach.
              I never stopped studying, evolving, and teaching, eventually earning instructor certifications in Jeet Kune Do and Kali Escrima.
            </p>
            <p>
              I do this because I love this world. Because I truly believe martial arts can transform people into the best version of themselves: stronger, calmer, more aware.
              Every class, every student, every moment is a chance to help someone rebuild confidence, balance, and safety.
            </p>
            <p><strong>That is my "why."</strong> It's what drives me every day.</p>
          </div>

          <div className="timeline">
            <h4>Martial Arts Journey Timeline</h4>
            <div className="timeline-item">
              <div className="timeline-year">Age 4-15 (11 years)</div>
              <div className="timeline-text">Shotokan Karate training, competing at national and world championships, developing discipline and foundational skills</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2012</div>
              <div className="timeline-text">Discovered Jeet Kune Do, traveled to California for intensive training under Dan Inosanto lineage instructors</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2012-2020 (8 years)</div>
              <div className="timeline-text">Cross-training in Boxing, Muay Thai, and Filipino Martial Arts (Kali/Escrima)</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2015</div>
              <div className="timeline-text">Relocated to Zurich, Switzerland; began teaching JKD privately</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2018-Present</div>
              <div className="timeline-text">Brazilian Jiu-Jitsu training, earned Blue Belt, integrated grappling into JKD curriculum</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2020</div>
              <div className="timeline-text">Earned Full Instructor Certification in Jeet Kune Do</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2021</div>
              <div className="timeline-text">Founded J.K.D. Functional Defense, opened dedicated training facility in Wiedikon</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">Present</div>
              <div className="timeline-text">Leading J.K.D. Functional Defense with 50+ active students, conducting workshops and seminars across Switzerland</div>
            </div>
          </div>

          <div className="story-section">
            <h3>Teaching Philosophy</h3>
            <p>
              Based in Zurich since 2015, I am committed to making JKD accessible to everyone regardless of experience level. My teaching emphasizes <strong>safety, proper technique, and creating an inclusive environment</strong> where students can grow at their own pace.
            </p>
            <p>
              What sets my approach apart is that I don't just teach techniques—I teach the <strong>"why" behind every movement</strong>. Understanding the principles allows students to adapt and apply JKD in real situations, rather than just memorizing patterns. Every student is different, and I believe in meeting each person where they are and helping them discover their own martial arts journey.
            </p>
            <p>
              Whether you're a complete beginner looking for practical self-defense, a former athlete seeking a new challenge, or an experienced martial artist wanting to expand your knowledge, I'm here to guide you. My goal isn't to create carbon copies of myself, but to help each student develop their own expression of JKD that fits their body type, personality, and goals.
            </p>
          </div>

          <div className="philosophy-box">
            <h3>My Mission</h3>
            <p>
              "I believe everyone has the right to feel safe, strong, and clear-minded. Functional defense is a tool for living with greater confidence and freedom. My mission is to empower individuals through martial arts, helping them build not just fighting skills, but life skills: confidence, resilience, discipline, and self-awareness."
            </p>
          </div>

          <div className="story-section">
            <h3>Beyond the Gym</h3>
            <p>
              When I'm not training or teaching, you'll find me exploring the mountains around Zurich, practicing mindfulness meditation, or studying philosophy and psychology. I believe martial arts is just one part of a holistic approach to personal development. Physical training must be balanced with mental and emotional growth.
            </p>
            <p>
              I'm also passionate about making martial arts accessible to underrepresented groups. I regularly conduct <strong>women's self-defense workshops</strong> and have partnered with local community centers to offer subsidized training for youth from disadvantaged backgrounds. Martial arts changed my life, and I want to pay that forward.
            </p>
          </div>

          <div style={{ textAlign: 'center', marginTop: '50px', padding: '30px', backgroundColor: 'var(--bg-card)', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '1.8rem', marginBottom: '15px', color: 'var(--text-primary)' }}>Train With Me</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '25px', fontSize: '1rem' }}>
              Ready to start your journey in Jeet Kune Do? Join us for a free trial class and experience firsthand how JKD can transform your life.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="#contact" className="btn" onClick={onClose}>Book Your Free Trial</a>
              <a href="#footer-classes" className="btn btn-secondary" onClick={onClose}>View Class Schedule</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default InstructorDrawer;
